import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useCart } from "../cart";
import { getActiveReferral, getActiveDropCode } from "../referral";
import { track, trackFunnel } from "../analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CartDrawer() {
    const { items, open, setOpen, removeItem, updateQuantity, subtotal, count, clear } = useCart();
    const [loading, setLoading] = useState(false);
    const [email, setEmail] = useState("");

    const captureCart = async (emailToSave) => {
        const cleaned = (emailToSave || "").trim().toLowerCase();
        if (!cleaned || !cleaned.includes("@") || !items.length) return;
        try {
            await axios.post(`${API}/cart/capture`, {
                email: cleaned,
                items: items.map((i) => ({
                    product_id: i.product_id,
                    name: i.name,
                    quantity: i.quantity,
                    size: i.size,
                    color: i.color,
                    price: i.price,
                })),
            });
        } catch {
            /* silent — non-blocking abandoned-cart capture */
        }
    };

    const checkout = async () => {
        if (!items.length) return;
        setLoading(true);
        // Fire abandoned-cart capture in parallel (non-blocking)
        if (email) captureCart(email);
        track("begin_checkout", {
            currency: "USD",
            value: subtotal,
            num_items: count,
            items: items.map((i) => ({
                item_id: i.product_id,
                item_name: i.name,
                item_variant: [i.color, i.size].filter(Boolean).join(" / "),
                price: i.price,
                quantity: i.quantity,
            })),
        });
        trackFunnel("checkout_clicked");
        try {
            const payload = {
                items: items.map((i) => ({
                    product_id: i.product_id,
                    size: i.size,
                    color: i.color || null,
                    quantity: i.quantity,
                })),
                origin_url: window.location.origin,
                customer_email: email && email.includes("@") ? email.trim().toLowerCase() : undefined,
                referral_code: getActiveReferral() || undefined,
                drop_code: getActiveDropCode() || undefined,
            };
            const res = await axios.post(`${API}/checkout`, payload);
            if (res.data?.url) {
                window.location.href = res.data.url;
            } else {
                toast.error("Couldn't start checkout. Try again.");
                setLoading(false);
            }
        } catch (err) {
            const msg = err?.response?.data?.detail || "Checkout failed. Try again.";
            toast.error(typeof msg === "string" ? msg : "Checkout failed");
            setLoading(false);
        }
    };

    return (
        <>
            {/* Floating cart button */}
            <button
                data-testid="cart-toggle"
                onClick={() => setOpen(true)}
                className="fixed bottom-6 right-6 z-40 bg-white text-black w-14 h-14 flex items-center justify-center shadow-2xl hover:scale-105 transition-transform"
                aria-label="Open cart"
            >
                <span className="font-display text-xl">CART</span>
                {count > 0 && (
                    <span
                        data-testid="cart-count"
                        className="absolute -top-2 -right-2 bg-[#E63946] text-white text-xs font-display w-6 h-6 flex items-center justify-center"
                    >
                        {count}
                    </span>
                )}
            </button>

            {/* Drawer overlay */}
            {open && (
                <div
                    data-testid="cart-drawer-overlay"
                    onClick={() => setOpen(false)}
                    className="fixed inset-0 z-[55] bg-black/70 backdrop-blur-sm"
                />
            )}

            {/* Drawer */}
            <aside
                data-testid="cart-drawer"
                className={`fixed top-0 right-0 z-[56] h-full w-full sm:w-[420px] bg-fgm-surface border-l border-white/10 transform transition-transform duration-300 flex flex-col ${
                    open ? "translate-x-0" : "translate-x-full"
                }`}
            >
                <div className="p-6 border-b border-white/10 flex items-center justify-between">
                    <div>
                        <div className="fgm-stripe-divider w-12 mb-2" />
                        <h3 className="font-display text-2xl text-white uppercase tracking-wide">
                            Your Cart
                        </h3>
                    </div>
                    <button
                        data-testid="cart-close"
                        onClick={() => setOpen(false)}
                        className="text-zinc-400 hover:text-white text-3xl leading-none"
                        aria-label="Close cart"
                    >
                        ×
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-6 space-y-5">
                    {items.length === 0 ? (
                        <div className="text-center py-16">
                            <div className="font-display text-zinc-600 tracking-[0.3em] text-sm uppercase mb-3">
                                Cart Is Empty
                            </div>
                            <p className="text-zinc-500 text-sm mb-6">
                                Pick a piece from the drop and let's go.
                            </p>
                            <button
                                onClick={() => {
                                    setOpen(false);
                                    setTimeout(() => {
                                        document.getElementById("shop")?.scrollIntoView({ behavior: "smooth" });
                                    }, 200);
                                }}
                                className="btn-outline"
                            >
                                Browse Drop
                            </button>
                        </div>
                    ) : (
                        items.map((item) => (
                            <div
                                key={`${item.product_id}-${item.size}-${item.color || ""}`}
                                data-testid={`cart-item-${item.product_id}`}
                                className="flex gap-4 pb-5 border-b border-white/10 last:border-0"
                            >
                                <img
                                    src={item.image}
                                    alt={item.name}
                                    className="w-20 h-24 object-cover flex-shrink-0 bg-black"
                                 loading="lazy" decoding="async" />
                                <div className="flex-1 min-w-0">
                                    <div className="font-display text-white uppercase text-base leading-tight mb-1 truncate">
                                        {item.name}
                                    </div>
                                    <div className="text-xs text-zinc-500 font-display tracking-widest uppercase mb-3">
                                        Size {item.size}{item.color ? ` · ${item.color}` : ""}
                                    </div>
                                    <div className="flex items-center justify-between gap-2">
                                        <div className="inline-flex items-center border border-white/10">
                                            <button
                                                data-testid={`cart-dec-${item.product_id}`}
                                                onClick={() =>
                                                    updateQuantity(item.product_id, item.size, item.quantity - 1, item.color)
                                                }
                                                className="px-2 py-1 text-white hover:bg-white/10"
                                            >
                                                −
                                            </button>
                                            <span className="px-3 text-sm text-white font-display">
                                                {item.quantity}
                                            </span>
                                            <button
                                                data-testid={`cart-inc-${item.product_id}`}
                                                onClick={() =>
                                                    updateQuantity(item.product_id, item.size, item.quantity + 1, item.color)
                                                }
                                                className="px-2 py-1 text-white hover:bg-white/10"
                                            >
                                                +
                                            </button>
                                        </div>
                                        <div className="font-display text-white">
                                            ${(item.price * item.quantity).toFixed(2)}
                                        </div>
                                    </div>
                                    <button
                                        data-testid={`cart-remove-${item.product_id}`}
                                        onClick={() => removeItem(item.product_id, item.size, item.color)}
                                        className="mt-2 text-xs text-zinc-500 hover:text-[#E63946] font-display tracking-widest uppercase"
                                    >
                                        Remove
                                    </button>
                                </div>
                            </div>
                        ))
                    )}
                </div>

                {items.length > 0 && (
                    <div className="p-6 border-t border-white/10 space-y-4">
                        <div className="flex items-center justify-between">
                            <span className="font-display text-zinc-400 tracking-widest uppercase">
                                Subtotal
                            </span>
                            <span data-testid="cart-subtotal" className="font-display text-2xl text-white">
                                ${subtotal.toFixed(2)}
                            </span>
                        </div>
                        <p className="text-xs text-zinc-500">
                            Shipping & taxes calculated at checkout.
                        </p>
                        <div className="space-y-1.5">
                            <label className="font-display text-[10px] tracking-[0.3em] uppercase text-zinc-500 block">
                                Email for order updates
                            </label>
                            <input
                                data-testid="cart-email-input"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                onBlur={(e) => captureCart(e.target.value)}
                                placeholder="you@email.com"
                                className="w-full bg-black border border-white/15 px-3 py-2 text-sm text-white focus:border-[#E63946] focus:outline-none"
                                autoComplete="email"
                            />
                        </div>
                        <button
                            data-testid="cart-checkout"
                            onClick={checkout}
                            disabled={loading}
                            className="btn-primary w-full disabled:opacity-50"
                        >
                            {loading ? "Redirecting…" : "Checkout →"}
                        </button>
                        <button
                            data-testid="cart-clear"
                            onClick={clear}
                            className="w-full text-xs font-display tracking-[0.3em] uppercase text-zinc-500 hover:text-white"
                        >
                            Clear Cart
                        </button>
                    </div>
                )}
            </aside>
        </>
    );
}
